<template>
    <BasicHeader pretitle="aqfer Marketing Data Platform" title="Sandbox Credentials"
        :buttonText="credentialManager == 'lakeview' ? 'Attach Credential' : 'Add Credential'"
        @btnClick="showAddSandboxCredential" :buttonText2="credentialManager == 'lakeview' ? 'Refresh' : ''"
        buttonClass2="btn-primary" @btnClick2="onRefreshClick"
        :buttonRoles="credentialManager == 'lakeview' ? allowedUserRoles : aquacredAllowedUserRoles" />
    <div v-if="loading && !sandboxOptions.length" class="text-center">
        <div class="spinner-border">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
    <div class="container-fluid" v-else-if="sandboxOptions.length > 0">
        <div class="row">
            <div class="col-md-6 col-lg-4 mb-2">
                <InputField label="Choose a Sandbox" type="select" :options="sandboxOptions"
                    v-model="selectedSandbox" />
            </div>
        </div>
        <div class="alert alert-danger" role="alert" v-if="error">
            {{ error }}
        </div>
        <div class="card">
            <Datatable v-if="credentialManager == 'aquacred'" tableId="aquacredSandboxCredentials"
                :columns="aquacredColumns" uniqueField="name" :data="credentialList"
                @deleteRows="onDeleteAquacredCredentials" :deletable="isCredentialDelete" deleteTxt="Delete"
                :selectCheckboxes="isCredentialDelete" :compact="true"
                :detailColumn="{ baseUrl: 'credentials/', name: 'Sandbox Credential', identifier: 'name' }"
                :showLoading="loading" :ignoreCache="true" />
            <Datatable v-else tableId="sandboxCredentials" :columns="columns" uniqueField="client_credential_name"
                :data="credentialList" @deleteRows="showDetachSandboxCredential" :deletable="isCredentialDelete"
                deleteTxt="Detach" :selectCheckboxes="isCredentialDelete" :formatColumn="formatColumn" :compact="true"
                :detailColumn="{ baseUrl: 'credentials/', name: 'Sandbox Credential', identifier: 'client_credential_name' }"
                :showLoading="loading" :ignoreCache="true" />
        </div>
    </div>
    <div v-else class="container-fluid">
        <div class="alert alert-secondary" role="alert">
            No Sandbox available!
        </div>
    </div>
</template>

<script>
import BasicHeader from "../../components/BasicHeader.vue";
import Datatable from "../../components/Datatable.vue";
import InputField from "../../components/InputField.vue";
import { mapActions, mapState } from "vuex";
import { getCredentialType } from "../../utils/aquacred";
export default {
    components: { Datatable, BasicHeader, InputField },
    name: "SandboxCredentials",
    data() {
        return {
            columns: [
                {
                    data: "client_credential_name",
                    title: "Client Credential Name",
                },
                {
                    data: "sandbox_credential_name",
                    title: "Credential Name",
                },
                {
                    data: "type",
                    title: "Credential Type",
                },
                {
                    data: "attached_by",
                    title: "Attached By",
                },
                {
                    data: "attached_at",
                    title: "Attached At",
                },
                {
                    data: "status",
                }
            ],
            aquacredColumns: [
                {
                    data: "name",
                    title: "Credential Name",
                },
                {
                    data: "type",
                    title: "Credential Type"
                }
            ],
            loading: true,
            formatColumn: [
                {
                    index: 6,
                    type: "datetime"
                }
            ],
            error: null,
            showRefreshBtn: false,
            allowedUserRoles: [1, 1.5], // aqfer admin or aqfer manager can detach/attach credential to sandbox
            aquacredAllowedUserRoles: [1, 1.5, 3], //aqfer admin, aqfer manager or client admin can view aquacred credentials
        };
    },
    computed: {
        ...mapState({
            sandboxDetails: state => state.sandbox.sandboxDetails,
            sandboxCredentials: state => state.amdp.sandboxCredentials,
            aquacredSandboxCredentials: state => state.amdp.aquacredSandboxCredentials
        }),
        credentialManager() {
            return this.sandboxDetails[this.selectedSandbox]?.credential_manager || 'lakeview';
        },
        selectedSandbox: {
            get() {
                return this.$store.state.sandbox.currentSandbox;
            },
            set(value) {
                if (value) {
                    this.$store.dispatch('sandbox/setCurrentSandbox', value);
                    this.$router.push({
                        name: "Sandbox Credentials",
                        params: {
                            sandboxId: `${value}`
                        },
                    });
                }
            }
        },
        sandboxOptions() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes ? this.$store.state.sandbox.devSandboxes : [];
            let prodV2Sandboxes = this.$store.state.sandbox.prodV2Sandboxes ? this.$store.state.sandbox.prodV2Sandboxes : [];
            let sandboxes = [...devSandboxes, ...prodV2Sandboxes]
            return sandboxes.map((sandbox) => {
                return { "label": sandbox.name + " - " + sandbox.env, "value": sandbox.name };
            });
        },
        credentialList() {
            let creds = this.$store.state.amdp.credentials;
            let self = this;
            let credList = [];

            // fetch appropriate credentials list based on credential manager type defined in sandbox details
            if (this.credentialManager == 'aquacred') {
                credList = this.aquacredSandboxCredentials || [];
                credList.forEach(cred => {
                    cred["type"] = getCredentialType(cred);
                });
            } else {
                const list = this.sandboxCredentials || [];
                credList = list.map((sandboxCred) => {
                    const name = sandboxCred.client_credential_name;
                    const matchCred = creds.find(cred => cred.name == name);
                    if (matchCred) {
                        sandboxCred["status"] = `<span class='text-success'>●</span> Up-to-date`;
                    } else {
                        this.showRefreshBtn = true;
                        sandboxCred["status"] = `<span class='text-danger'>●</span> Stale`;
                    }
                    return sandboxCred;
                })
            }
            return credList?.length > 0 ? credList : null;
        },
        sandboxCredList() {
            return this.credentialList ? this.credentialList.map((cred) => cred.client_credential_name) : []
        },
        sandboxCredListAlias() {
            let alias = {}
            if (this.credentialList) {
                this.credentialList.map((cred) => { alias[cred.client_credential_name] = cred.sandbox_credential_name })
            }
            return alias;
        },
        isCredentialDelete() {
            if (this.credentialManager == 'aquacred') return this.isAccessByUserRole(this.aquacredAllowedUserRoles);
            else return this.isAccessByUserRole(this.allowedUserRoles);
        },
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            getSandboxCredentials: "amdp/getSandboxCredentials",
            fetchCredentials: "amdp/fetchCredentials",
            fetchAquacredCredentials: "amdp/fetchAquacredCredentials",
            deleteAquacredCredentials: "amdp/deleteAquacredCredentials",
        }),
        handleSelect(data) {
            this.$store.dispatch("amdp/setCurrentCredential", data);
        },
        showDeleteCredentials(rows) {
            this.showModal({ component: "DeleteCredentials", data: rows });
        },
        showAddSandboxCredential() {
            if (this.credentialManager == 'aquacred') {
                this.showModal({
                    component: 'AddAquacredCredential',
                    data: {
                        sandbox: this.selectedSandbox,
                        refreshCredentials: this.refreshAquacredCredentials
                    }
                })
            } else {
                this.showModal({
                    component: 'AddSandboxCredentials',
                    data: {
                        sandboxOptions: this.sandboxOptions,
                        selectedSandbox: this.selectedSandbox,
                        sandboxCredList: this.sandboxCredList,
                        sandboxCredListAlias: this.sandboxCredListAlias
                    }
                })
            }
        },
        async onDeleteAquacredCredentials(creds) {
            let self = this;
            this.$store.dispatch('showModal', {
                component: "confirm",
                data: {
                    title: "Delete Credential(s)",
                    message: `
                        Are you sure you want to <strong class="text-danger">DELETE</strong> the following credentials?
                        <div class="delete-creds-container">
                            <ul class="list-group list-group-flush">
                                ${creds.map(cred => `
                                    <li class="list-group-item py-2 delete-cred-item fs-5">
                                        <i class="fa-solid fa-key me-2 opacity-75 text-primary"></i>
                                        <span class="fw-bold">${cred.name}</span>
                                    </li>
                                `).join('')}
                            </ul>
                        </div>`,
                    confirmText: 'Delete',
                    confirmClass: 'btn-danger',
                    cancelText: 'Cancel',
                    cancelClass: 'btn-secondary',
                    async confirm() {
                        try {
                            // use promise for batch delete
                            const deletePromises = creds.map(cred => {
                                self.deleteAquacredCredentials({
                                    service: 'amdp',
                                    namespace: self.selectedSandbox,
                                    credentialName: cred.name
                                });
                            });
                            const results = await Promise.allSettled(deletePromises);

                            let successfulDeletions = 0
                            results.forEach((res, idx) => {
                                const cred = creds[idx];

                                if (res.status === "fulfilled") {
                                    console.log(`Deleted credential: ${cred.name}`);
                                    successfulDeletions++;
                                } else {
                                    console.error(`Failed to delete credential: ${cred.name}\nReason: ${res.reason.response?.data}`);
                                    let toastoptions = {
                                        message: res.reason.response?.data || "Error deleteing credential",
                                        type: "error",
                                    };
                                    self.$store.dispatch('addToast', toastoptions);
                                }
                            });

                            if (successfulDeletions == creds.length) {
                                let toastoptions = {
                                    message: `Credential${creds.length > 1 ? 's' : ''} deleted successfully`,
                                    type: "success",
                                };
                                self.$store.dispatch('addToast', toastoptions);
                            }

                            // refresh credentials list
                            await self.refreshAquacredCredentials();
                        } catch (error) {
                            let err = error?.response?.data?.error || error?.message;
                            console.error(err);
                            self.$store.dispatch('addToast', {
                                type: 'error',
                                message: "Something went wrong while deleting credentials. Please contact support.",
                            });
                        }
                    }
                }
            });
        },
        showDetachSandboxCredential(rows) {
            let credNames = rows.map((val) => val.client_credential_name)
            this.showModal({ component: 'DetachSandboxCredentials', data: { sandboxName: this.selectedSandbox, rows, credNames: { "remove_credentials": credNames } } })
        },
        isAccessByUserRole(roles) {
            return this.$store.getters.userRoles.isAccessByRole(roles);
        },
        onRefreshClick() {
            this.showModal({
                component: 'AddSandboxCredentials',
                data: {
                    selectedSandbox: this.selectedSandbox,
                    sandboxCredList: this.sandboxCredList,
                    sandboxCredListAlias: this.sandboxCredListAlias,
                    refresh: true
                }
            })
        },
        async refreshAquacredCredentials() {
            this.loading = true;
            await this.fetchAquacredCredentials({
                service: 'amdp',
                namespace: this.selectedSandbox
            }).finally(() => {
                this.loading = false;
            });
        },
    },
    async beforeMount() {
        try {
            if (this.credentialManager === 'aquacred') {
                await this.fetchAquacredCredentials({
                    service: 'amdp',
                    namespace: this.selectedSandbox
                });
            } else {
                this.fetchCredentials();
                await this.getSandboxCredentials(this.selectedSandbox);
            }
        } catch (error) {
            this.error = error?.response?.data?.error ?? error.message;
        } finally {
            this.loading = false;
        }
    },
};
</script>

<style lang="scss">
/* Container to prevent modal blowout when deleting multiple items */
.delete-creds-container {
    max-height: 300px;
    overflow-y: auto;
    margin-top: 0.75rem;
}

/* Styled line items with monospace presentation */
.delete-cred-item {
    word-break: break-all;
}
</style>